import { type ErrorClassification, ErrorClassificationDisplayName } from "../sentry/classification";
import { SentryError } from "../sentry/SentryError";
import { type ErrorDetails, RemiDomain, RemiDomainDisplayName } from "./errorTypes/domain";
import { accountingErrorDetails } from "./errorTypes/domains/accounting";
import { adminErrorDetails } from "./errorTypes/domains/admin";
import { agentActionErrorDetails } from "./errorTypes/domains/agentAction";
import { aiErrorDetails } from "./errorTypes/domains/ai";
import { areaCodeErrorDetails } from "./errorTypes/domains/areaCode";
import { authErrorDetails } from "./errorTypes/domains/auth";
import { bidErrorDetails } from "./errorTypes/domains/bid";
import { bouncieErrorDetails } from "./errorTypes/domains/bouncie";
import { communicationsErrorDetails } from "./errorTypes/domains/communications";
import { complianceErrorDetails } from "./errorTypes/domains/compliance";
import { configErrorDetails } from "./errorTypes/domains/config";
import { contractorErrorDetails } from "./errorTypes/domains/contractor";
import { documentErrorDetails } from "./errorTypes/domains/document";
import { dropboxSignErrorDetails } from "./errorTypes/domains/dropboxSign";
import { escalationErrorDetails } from "./errorTypes/domains/escalation";
import { estimateErrorDetails } from "./errorTypes/domains/estimate";
import { eventStoreErrorDetails } from "./errorTypes/domains/eventStore";
import { externalErrorDetails } from "./errorTypes/domains/external";
import { financeErrorDetails } from "./errorTypes/domains/finance";
import { geographicAreaErrorDetails } from "./errorTypes/domains/geographicArea";
import { holdupErrorDetails } from "./errorTypes/domains/holdup";
import { marketingErrorDetails } from "./errorTypes/domains/marketing";
import { materialsErrorDetails } from "./errorTypes/domains/materials";
import { mobileErrorDetails } from "./errorTypes/domains/mobile";
import { partnerIntegrationErrorDetails } from "./errorTypes/domains/partnerIntegration";
import { personErrorDetails } from "./errorTypes/domains/person";
import { priceErrorDetails } from "./errorTypes/domains/price";
import { projectErrorDetails } from "./errorTypes/domains/project";
import { propertyModelErrorDetails } from "./errorTypes/domains/propertyModel";
import { quotingErrorDetails } from "./errorTypes/domains/quoting";
import { regionErrorDetails } from "./errorTypes/domains/region";
import { scheduledEventsErrorDetails } from "./errorTypes/domains/scheduledEvents";
import { scopeOfWorkErrorDetails } from "./errorTypes/domains/scopeOfWork";
import { sunrunApiErrorDetails } from "./errorTypes/domains/sunrun-api";
import { takeRateErrorDetails } from "./errorTypes/domains/take-rate";
import { taskErrorDetails } from "./errorTypes/domains/task";
import { ticketErrorDetails } from "./errorTypes/domains/ticket";
import { webhookSenderErrorDetails } from "./errorTypes/domains/webhook-sender";
import { workSiteErrorDetails } from "./errorTypes/domains/work-site";
import type { ServiceErrorInput } from "./serviceErrorInput";

/**
 * This error will become the standard for our service layer. It provides useful error messages that we can manage in sentry
 * @param severity will determine incident response that Remi takes. Use with caution
 * @param classification is a higher level error group. These are general categories that routers, scheduled jobs, etc, can use to handle errors
 * @param domain is the domain of the error
 * @param errorType is a specific, domain-specific error. These can be reused, but it should be descriptive enough to know exactly what happened based on the domain and error type
 * @param data is an object tied to an errorType. This should be used to provide necessary context, like the id of the failed record, webhook url, etc. Must be serializable.
 */
export class RemiServiceError extends SentryError<
  RemiDomain,
  ServiceErrorInput["errorType"],
  ServiceErrorInput["data"]
> {
  private readonly _classification: ErrorClassification;
  /**
   * @deprecated This flag is deprecated and will be removed in a future version.
   * It was used for backwards compatibility during migration to identify errors that should be thrown instead of logged.
   * New code should not use this flag.
   */
  private readonly _isPotentialBlockingError?: boolean;

  // Once fully migrated, input will always be a ServiceErrorInput
  // class members will then become required
  constructor(input: ServiceErrorInput) {
    const errorDetails = getErrorDetails(input);
    const name = `${RemiDomainDisplayName[input.domain]} Domain · ${input.errorType}`;
    const customFingerprintPrefix = input.fingerprintPrefix ? `${input.fingerprintPrefix} · ` : "";
    const message = `${ErrorClassificationDisplayName[errorDetails.classification]} · ${
      errorDetails.sentryDescription
    }`;
    super({
      name,
      message,
      severity: input.severity,
      service: input.domain,
      errorType: input.errorType,
      data: input.data,
      publicMessage: input.publicMessage ?? errorDetails.sentryDescription,
      customFingerprintPrefix,
      metric: input.metric,
    });

    this._classification = errorDetails.classification;
    this._isPotentialBlockingError = input.isPotentialBlockingError;
  }

  getSentryData() {
    const tags: Record<string, string | boolean> = {
      severity: this.severity,
      remi_service: this.service,
      error_type: this.errorType,
      classification: this.classification,
    };
    if (this.token) {
      tags.token = this.token;
    }
    if (this.origin) {
      tags.origin = this.origin;
    }
    if (this.isPotentialBlockingError) {
      tags.is_potential_blocking_error = this.isPotentialBlockingError;
    }
    return {
      tags,
      attributes: {
        errorData: this.reportedData,
      },
      fingerprint: [this.service, this.errorType, this.severity],
    };
  }

  get isPotentialBlockingError(): boolean | undefined {
    return this._isPotentialBlockingError;
  }

  get classification(): ErrorClassification {
    return this._classification;
  }
}

function getErrorDetails(input: ServiceErrorInput): ErrorDetails {
  switch (input.domain) {
    case RemiDomain.ADMIN:
      return adminErrorDetails[input.errorType];
    case RemiDomain.ACCOUNTING:
      return accountingErrorDetails[input.errorType];
    case RemiDomain.AGENT_ACTION:
      return agentActionErrorDetails[input.errorType];
    case RemiDomain.AI:
      return aiErrorDetails[input.errorType];
    case RemiDomain.AREA_CODE:
      return areaCodeErrorDetails[input.errorType];
    case RemiDomain.AUTH:
      return authErrorDetails[input.errorType];
    case RemiDomain.BID:
      return bidErrorDetails[input.errorType];
    case RemiDomain.BOUNCIE:
      return bouncieErrorDetails[input.errorType];
    case RemiDomain.COMMUNICATIONS:
      return communicationsErrorDetails[input.errorType];
    case RemiDomain.COMPLIANCE:
      return complianceErrorDetails[input.errorType];
    case RemiDomain.CONFIG:
      return configErrorDetails[input.errorType];
    case RemiDomain.CONTRACTOR:
      return contractorErrorDetails[input.errorType];
    case RemiDomain.ESCALATION:
      return escalationErrorDetails[input.errorType];
    case RemiDomain.DOCUMENT:
      return documentErrorDetails[input.errorType];
    case RemiDomain.DROPBOX_SIGN:
      return dropboxSignErrorDetails[input.errorType];
    case RemiDomain.ESTIMATE:
      return estimateErrorDetails[input.errorType];
    case RemiDomain.EVENT_STORE:
      return eventStoreErrorDetails[input.errorType];
    case RemiDomain.FINANCE:
      return financeErrorDetails[input.errorType];
    case RemiDomain.GEOGRAPHIC_AREA:
      return geographicAreaErrorDetails[input.errorType];
    case RemiDomain.HOLDUP:
      return holdupErrorDetails[input.errorType];
    case RemiDomain.MARKETING:
      return marketingErrorDetails[input.errorType];
    case RemiDomain.PARTNER_INTEGRATION:
      return partnerIntegrationErrorDetails[input.errorType];
    case RemiDomain.PERSON:
      return personErrorDetails[input.errorType];
    case RemiDomain.PROJECT:
      return projectErrorDetails[input.errorType];
    case RemiDomain.PROPERTY_MODEL:
      return propertyModelErrorDetails[input.errorType];
    case RemiDomain.QUOTING:
      return quotingErrorDetails[input.errorType];
    case RemiDomain.REGION:
      return regionErrorDetails[input.errorType];
    case RemiDomain.SCHEDULED_EVENTS:
      return scheduledEventsErrorDetails[input.errorType];
    case RemiDomain.SCOPE_OF_WORK:
      return scopeOfWorkErrorDetails[input.errorType];
    case RemiDomain.SUNRUN_API:
      return sunrunApiErrorDetails[input.errorType];
    case RemiDomain.TASK:
      return taskErrorDetails[input.errorType];
    case RemiDomain.EXTERNAL:
      return externalErrorDetails[input.errorType];
    case RemiDomain.TICKET:
      return ticketErrorDetails[input.errorType];
    case RemiDomain.MOBILE:
      return mobileErrorDetails[input.errorType];
    case RemiDomain.PRICE:
      return priceErrorDetails[input.errorType];
    case RemiDomain.MATERIALS:
      return materialsErrorDetails[input.errorType];
    case RemiDomain.TAKE_RATE:
      return takeRateErrorDetails[input.errorType];
    case RemiDomain.WEBHOOK_SENDER:
      return webhookSenderErrorDetails[input.errorType];
    case RemiDomain.WORK_SITE:
      return workSiteErrorDetails[input.errorType];
    // Do not add a default case to this switch statement so that lint fails of the list is not exhaustive
  }
}
